import { BaseTableEntry } from 'src/auth/TypeDefinitions';
import { storageConstants } from './AuthConstants';
import { logConstants } from '../utils/Constants';
import { Logger } from './Logger';
import { SentryLogger } from '../sentryLogging/SentryLogger';
import { AuthStorageErrorCodes, AuthStorageInitError } from './AuthErrors';

export class AuthStorage {
	static readonly AUTH_STORE: string = 'CitrixAuthDatabase';
	static readonly AUTH_STORE_VERSION = 7;
	static readonly DEFAULT_MAX_DB_REINIT_RETRIES = 1;
	private retries = AuthStorage.DEFAULT_MAX_DB_REINIT_RETRIES;

	db: IDBDatabase;
	DBOpenRequest: IDBOpenDBRequest;

	private constructor(readonly _dbFactory: any, readonly maxDbReinitRetries: number) {
		this.retries = maxDbReinitRetries;
	}

	public static async createInstance(
		dbFactory: any,
		maxDbReinitRetries = AuthStorage.DEFAULT_MAX_DB_REINIT_RETRIES
	): Promise<AuthStorage> {
		const authStorageInstance = new AuthStorage(dbFactory, maxDbReinitRetries);
		try {
			await authStorageInstance.init();
		} catch (errorObj) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Auth DB initialization failed, errorCode: ${errorObj.errorCode},  ${errorObj.message}`
			);
			await AuthStorage.handleInitError(errorObj, authStorageInstance);
		}
		return authStorageInstance;
	}

	static async handleInitError(
		errorObj: unknown,
		AuthStorageInstance: AuthStorage
	): Promise<void> {
		if (
			(errorObj as { errorCode: number }).errorCode ===
			AuthStorageErrorCodes.AML_DB_OPEN_FAILURE_DELETED_DB_SUCCESSFULLY
		) {
			try {
				//since we've deleted datatbase trying to perform init again
				Logger.getInstance().write(
					logConstants.INFO,
					'attempting to initialize auth DB again'
				);
				await AuthStorageInstance.init();
			} catch (errorObj1) {
				let errorCode1 = (errorObj1 as { errorCode: number }).errorCode;
				Logger.getInstance().write(
					logConstants.ERROR,
					`second attempt to initialize auth DB failed, errorCode: ${errorCode1}`
				);
				errorCode1 =
					errorCode1 !== undefined && errorCode1 !== null
						? errorCode1
						: AuthStorageErrorCodes.AML_DB_INIT_UNKNOWN_ERROR;

				if (errorCode1 === AuthStorageErrorCodes.AML_DB_INIT_UNKNOWN_ERROR) {
					SentryLogger.getInstance().logError(errorObj1);
				}

				throw new AuthStorageInitError(
					errorCode1,
					'second attempt to initialize auth DB failed'
				);
			}
		} else if (
			(errorObj as { errorCode: number }).errorCode ===
			AuthStorageErrorCodes.AML_DB_OPEN_FAILURE
		) {
			throw new AuthStorageInitError(
				AuthStorageErrorCodes.AML_DB_OPEN_FAILURE,
				'Failed to open auth DB'
			);
		} else {
			throw new AuthStorageInitError(
				AuthStorageErrorCodes.AML_DB_INIT_UNKNOWN_ERROR,
				'Failed to open auth DB'
			);
		}
	}

	/// <summary>
	/// initialise indexedDB by opening the DB and attaching listeners for success, error and upgrade events
	/// </summary>
	async init(): Promise<void> {
		return new Promise(
			function (resolve: Function, reject: Function): void {
				this.DBOpenRequest = this._dbFactory.open(
					AuthStorage.AUTH_STORE,
					AuthStorage.AUTH_STORE_VERSION
				);
				this.DBOpenRequest['onerror'] = this.openRequestErrorHandler.bind(this, reject);
				this.DBOpenRequest['onsuccess'] = this.openRequestSuccess.bind(this, resolve);
				this.DBOpenRequest['onupgradeneeded'] = this.openRequestUpgradeNeeded.bind(this);
			}.bind(this)
		);
	}

	/// <summary>
	/// get the object store i.e table for performing a transaction
	/// </summary>
	async getObjectStore(
		tableName: string,
		mode: IDBTransactionMode
	): Promise<IDBObjectStore> {
		let transaction: IDBTransaction;
		try {
			transaction = this.db.transaction([tableName], mode);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`getObjectStore: transaction error: ${JSON.stringify({
					name: e.name,
					message: e.message,
				})}`
			);

			if (e.name === 'InvalidStateError' && this.retries > 0) {
				this.retries--;
				try {
					this.closeDBConnection();
					await this.init();
					return await this.getObjectStore(tableName, mode);
				} catch (errorObj) {
					Logger.getInstance().write(
						logConstants.ERROR,
						'getObjectStore: Failed to reinit : ' +
							JSON.stringify({
								name: errorObj.name,
								message: errorObj.message,
							})
					);
					throw errorObj;
				} finally {
					this.retries = this.maxDbReinitRetries;
				}
			} else {
				throw e;
			}
		}

		transaction.onerror = function (e): void {
			Logger.getInstance().write(
				logConstants.ERROR,
				'getObjectStore error: ' + JSON.stringify(e)
			);
		};
		transaction.onabort = function (): void {
			Logger.getInstance().write(logConstants.ERROR, 'getObjectStore abort: ');
		};
		const objectStore = transaction.objectStore(tableName);
		return objectStore;
	}

	openRequestErrorHandler(reject: Function): void {
		/* Keep this until we don't implement the upgrade scenario completely*/
		Logger.getInstance().write(
			logConstants.ERROR,
			`Error opening DB: ${this.DBOpenRequest['error']}`
		);
		const deleteRequest = indexedDB.deleteDatabase(AuthStorage.AUTH_STORE);
		deleteRequest.onerror = () => {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Error deleting DB:  ${deleteRequest['error']}`
			);
			reject({ errorCode: AuthStorageErrorCodes.AML_DB_OPEN_FAILURE });
		};
		deleteRequest.onsuccess = () => {
			Logger.getInstance().write(logConstants.INFO, `Deleted auth DB`);
			reject({
				errorCode: AuthStorageErrorCodes.AML_DB_OPEN_FAILURE_DELETED_DB_SUCCESSFULLY,
			});
		};
	}

	openRequestSuccess(callback: Function): void {
		this.db = this.DBOpenRequest['result'];
		this.db.addEventListener('close', () => {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Database connection closed unexpectedly`
			);
		});
		callback();
	}

	openRequestUpgradeNeeded(event: IDBVersionChangeEvent): void {
		this.db = this.DBOpenRequest['result'];
		const transaction = this.DBOpenRequest['transaction'];
		switch (event.oldVersion) {
			case 0: {
				// DB doesn't exist
				AuthStorage.setupVersionOneUpgrade(this.db);
			}
			case 1: {
				/*Adding a new index to DomainCookieTable and updating DB version to 2 for versioning so that it'll be executed
                  when DB is not present or when DB exist but it's on version 1.
                */
				AuthStorage.setupVersionTwoUpgrade(transaction);
			}
			case 2: {
				/*
                - Adding a new index to SecondaryTokenTable 
                - Modifying PrimaryTokenTable key (Migrates data to new table)
                */
				AuthStorage.setupVersionThreeUpgrade(this.db, transaction);
			}
			case 3: {
				/*
                - Adding a new index to SecondaryTokenTable 
                */
				AuthStorage.setupVersionFourUpgrade(transaction);
			}
			case 4: {
				AuthStorage.setupVersionFiveUpgrade(this.db);
			}
			case 5: {
				AuthStorage.setupVersionSixUpgrade(this.db);
			}
			case 6 : {
				AuthStorage.setupVersionSevenUpgrade(transaction);
			}
		}
	}

	/**
	 * Upgrade to version 7 makes the following changes to the DB:
	 * 1. Clears the DomainCookieTable
	 *  from version 7 onwards, we will be storing cookies in the in-memory storage
	 */
	static setupVersionSevenUpgrade(transaction: IDBTransaction): void {
		const domainCookiesStore = transaction.objectStore(
			storageConstants.DOMAIN_COOKIE_TABLE
		);
		domainCookiesStore.clear();
	}		

	/**
	 * Upgrade to version 6 makes the following changes to the DB:
	 * 1. Creates a new table `Credentials` to store the credentials entered for forms login.
	 * 2. Creates a new table `PrepopulatedCredentials` to store the credentials to be prepopulated in login form.
	 */
	static setupVersionSixUpgrade(db: IDBDatabase): void {
		const credentialsStore = db.createObjectStore(storageConstants.CREDENTIALS_TABLE, {
			keyPath: [storageConstants.AUTH_SERVICE_URL, storageConstants.REFERENCE_URI],
		});
		credentialsStore.createIndex(
			storageConstants.REFERENCE_URI,
			storageConstants.REFERENCE_URI,
			{ unique: false }
		);

		const prepopulatedCredentialsStore = db.createObjectStore(
			storageConstants.PREPOPULATED_CREDENTIALS_TABLE,
			{
				keyPath: [storageConstants.AUTH_SERVICE_URL, storageConstants.REFERENCE_URI],
			}
		);
		prepopulatedCredentialsStore.createIndex(
			storageConstants.REFERENCE_URI,
			storageConstants.REFERENCE_URI,
			{ unique: false }
		);
	}

	/**
	 * Upgrade to version 5 makes the following changes to the DB:
	 * 1. Creates a new table `OIDCEndpoints` to store the endpoints obtained from OIDC Discovery.
	 * 2. Creates a new table `OIDCTokens` to store OIDC access and refresh tokens.
	 * 3. Creates a new table `StoreConfiguration` to store configuration details (refer `DSAuth.setStoreConfiguration`).
	 */
	private static setupVersionFiveUpgrade(db: IDBDatabase): void {
		db.createObjectStore(storageConstants.OIDC_ENDPOINTS_TABLE, {
			keyPath: [
				storageConstants.SERVICE_URL,
				storageConstants.AUTH_DOMAIN,
				storageConstants.REFERENCE_URI,
			],
		});
		db.createObjectStore(storageConstants.OIDC_TOKENS_TABLE, {
			keyPath: [storageConstants.AUTH_DOMAIN, storageConstants.REFERENCE_URI],
		});
		db.createObjectStore(storageConstants.STORE_CONFIGURATION_TABLE, {
			keyPath: [storageConstants.REFERENCE_URI],
		});
	}

	/// <summary>
	/// Upgarde to version 4 makes following chages to DB:
	///     1. Creates a new tokenForService_referenceUri Index on secondary token table which supersedes tokenForService index
	/// </summary>
	static setupVersionFourUpgrade(transaction: IDBTransaction): void {
		const secondaryTokenStore = transaction.objectStore(
			storageConstants.SECONDARY_TOKEN_TABLE
		);
		secondaryTokenStore.createIndex(
			storageConstants.TOKEN_FOR_SERVICE_REFERENCE_URI,
			[storageConstants.TOKEN_FOR_SERVICE, storageConstants.REFERENCE_URI],
			{ unique: false }
		);
	}

	/// <summary>
	/// Upgarde to version 3 makes following chages to DB:
	///     1. Creates a new [authDomain, referenceURI] Index on SecondaryTokenTable
	///     2. Renames PrimaryTokenTable to OldPrimaryTokenTable
	///     3. Creates new PrimaryTokenTable with Keypath as [authDomain, referenceURI]
	///     4. Gets all data from OldPrimaryTokenTable and puts it to new PrimaryTokenTable
	///     5. Deletes OldPrimaryTokenTable
	/// </summary>
	static setupVersionThreeUpgrade(db: IDBDatabase, transaction: IDBTransaction): void {
		const secondaryTokenStore = transaction.objectStore(
			storageConstants.SECONDARY_TOKEN_TABLE
		);
		secondaryTokenStore.createIndex(
			storageConstants.AUTH_DOMAIN_REFERENCE_URI,
			[storageConstants.AUTH_DOMAIN, storageConstants.REFERENCE_URI],
			{ unique: false }
		);

		const primaryTokenStore = transaction.objectStore(
			storageConstants.PRIMARY_TOKEN_TABLE
		);
		primaryTokenStore.name = storageConstants.OLD + primaryTokenStore.name;
		const request = primaryTokenStore.getAll();
		request.onsuccess = function (event: Event): void {
			const eventTarget = event.target as DataBaseEventTarget;

			const oldEntries = eventTarget.result;
			const newPrimaryTokenStore = db.createObjectStore(
				storageConstants.PRIMARY_TOKEN_TABLE,
				{
					keyPath: [storageConstants.AUTH_DOMAIN, storageConstants.REFERENCE_URI],
				}
			);

			newPrimaryTokenStore.createIndex(
				storageConstants.AUTH_DOMAIN,
				storageConstants.AUTH_DOMAIN,
				{ unique: false }
			); // setting unique as false since multiple stores can have same authDomain
			newPrimaryTokenStore.createIndex(storageConstants.TOKEN, storageConstants.TOKEN, {
				unique: false,
			});
			newPrimaryTokenStore.createIndex(
				storageConstants.SERVICE_URL,
				storageConstants.SERVICE_URL,
				{ unique: false }
			);
			newPrimaryTokenStore.createIndex(
				storageConstants.REFERENCE_URI,
				storageConstants.REFERENCE_URI,
				{ unique: false }
			);
			for (const entry of oldEntries) {
				newPrimaryTokenStore.put(entry);
			}

			db.deleteObjectStore(storageConstants.OLD + storageConstants.PRIMARY_TOKEN_TABLE);
		};
	}

	/// <summary>
	/// Upgarde to version 2 makes following chages to DB:
	///     1. Creates a new referenceURI Index on Domain cookie table
	/// </summary>
	static setupVersionTwoUpgrade(transaction: IDBTransaction): void {
		const domainCookiesStore = transaction.objectStore(
			storageConstants.DOMAIN_COOKIE_TABLE
		);
		domainCookiesStore.createIndex(
			storageConstants.REFERENCE_URI,
			storageConstants.REFERENCE_URI,
			{ unique: false }
		);
	}

	/// <summary>
	/// Upgarde to version 1 makes following chages to DB:
	///     1. Create Primary token table with Keypath as referenceURI and Indexes as authDomain, token and serviceURL
	///     2. Create Secondary token table with Keypath as [serviceURL, authDomain, referenceURI] and Indexes as
	///            serivceURL_referenceURI, TokenforService and referenceURI
	///     3. Create Domain cookie table with Keypath as [domain, referenceURI]
	/// </summary>
	static setupVersionOneUpgrade(db: IDBDatabase): void {
		const primaryTokenStore = db.createObjectStore(storageConstants.PRIMARY_TOKEN_TABLE, {
			keyPath: storageConstants.REFERENCE_URI,
		});
		primaryTokenStore.createIndex(
			storageConstants.AUTH_DOMAIN,
			storageConstants.AUTH_DOMAIN,
			{ unique: false }
		); // setting unique as false since multiple stores can have same authDomain
		primaryTokenStore.createIndex(storageConstants.TOKEN, storageConstants.TOKEN, {
			unique: false,
		});
		primaryTokenStore.createIndex(
			storageConstants.SERVICE_URL,
			storageConstants.SERVICE_URL,
			{ unique: false }
		);
		const secondaryTokenStore = db.createObjectStore(
			storageConstants.SECONDARY_TOKEN_TABLE,
			{
				keyPath: [
					storageConstants.SERVICE_URL,
					storageConstants.AUTH_DOMAIN,
					storageConstants.REFERENCE_URI,
				],
			}
		);
		secondaryTokenStore.createIndex(
			storageConstants.SERVICE_URL_REFERENCE_URI,
			[storageConstants.SERVICE_URL, storageConstants.REFERENCE_URI],
			{ unique: false }
		);
		secondaryTokenStore.createIndex(
			storageConstants.TOKEN_FOR_SERVICE,
			storageConstants.TOKEN_FOR_SERVICE,
			{ unique: false }
		); //we're not using this index as tokenForService_referenceUri supersedes this index
		secondaryTokenStore.createIndex(
			storageConstants.REFERENCE_URI,
			storageConstants.REFERENCE_URI,
			{ unique: false }
		);

		db.createObjectStore(storageConstants.DOMAIN_COOKIE_TABLE, {
			keyPath: [storageConstants.DOMAIN, storageConstants.REFERENCE_URI],
		});
	}

	/// <summary>
	/// read one entry/row from given tabelName in indexedDB
	/// </summary>
	// searchWith parameter can be "key" or "index". Default value is "key"
	// searchFor parameter is the value of key or index corresponding to the searchWith parameter
	// indexName is an optional parameter. if searchWith="index", indexName must be provided
	async readEntry(
		tableName: string,
		searchFor: string | string[],
		searchWith = storageConstants.KEY,
		indexName?: string
	): Promise<BaseTableEntry | null> {
		return new Promise(
			async function (resolve: Function, reject: Function): Promise<void> {
				let objectStore;
				try {
					objectStore = await this.getObjectStore(tableName, 'readonly');
					if (searchWith == storageConstants.INDEX) {
						if (indexName) {
							objectStore = objectStore.index(indexName);
						} else {
							Logger.getInstance().write(
								logConstants.ERROR,
								'IndexName not provided for searchWith index'
							);
							reject(new Error('IndexName not provided for searchWith index'));
						}
					}
					const request = objectStore.get(searchFor);
					request.onerror = function (): void {
						reject(new Error('Error reading IndexedDB'));
					};
					request.onsuccess = function (event: DataBaseEvent): void {
						resolve(event.target.result);
					};
				} catch (e) {
					reject(e);
				}
			}.bind(this)
		);
	}

	/// <summary>
	/// read all entries/rows from given tableName in indexedDB
	/// </summary>
	async readAll(
		tableName: string,
		indexName?: string,
		indexValue?: string | string[]
	): Promise<BaseTableEntry[]> {
		return new Promise(
			async function (resolve: Function, reject: Function): Promise<void> {
				let objectStore;
				try {
					objectStore = await this.getObjectStore(tableName, 'readonly');
					let request;
					if (indexName && indexValue) {
						const myIndex = objectStore.index(indexName);
						request = myIndex.getAll(indexValue);
					} else {
						request = objectStore.getAll();
					}
					request.onsuccess = function (event: DataBaseEvent): void {
						resolve(event.target.result);
					};
					request.onerror = function (): void {
						reject(new Error('Error in IndexedDB readAll :' + tableName));
					};
				} catch (e) {
					reject(e);
				}
			}.bind(this)
		);
	}

	/// <summary>
	/// reads all the primary keys of all entries from given tableName in indexedDB
	/// </summary>
	async readAllKeys(tableName: string): Promise<string[][] | string[]> {
		return new Promise(
			async function (resolve: Function, reject: Function): Promise<void> {
				let objectStore;
				try {
					objectStore = await this.getObjectStore(tableName, 'readonly');
					const request = objectStore.getAllKeys();
					request.onsuccess = function (event: DataBaseEvent): void {
						resolve(event.target.result);
					};
					request.onerror = function (): void {
						reject(new Error('Error in IndexedDB readAllKeys :' + tableName));
					};
				} catch (e) {
					reject(e);
				}
			}.bind(this)
		);
	}

	/// <summary>
	/// delete one entry/row from given tabelName in indexedDB
	/// </summary>
	async deleteEntry(tableName: string, key: string | string[]): Promise<void> {
		return new Promise(
			async function (resolve: Function, reject: Function): Promise<void> {
				try {
					const objectStore = await this.getObjectStore(tableName, 'readwrite');
					const request = objectStore.delete(key);
					request.onsuccess = resolve;
					request.onerror = function (): void {
						Logger.getInstance().write(
							logConstants.ERROR,
							'Error deleting entry from IndexedDB'
						);
						reject(new Error('Error deleting entry from IndexedDB'));
					};
				} catch (e) {
					reject(e);
				}
			}.bind(this)
		);
	}

	//Update replaces existing entry if present
	async updateEntry(tableName: string, obj: BaseTableEntry): Promise<void> {
		return new Promise(
			async function (resolve: Function, reject: Function): Promise<void> {
				let objectStore;
				try {
					objectStore = await this.getObjectStore(tableName, 'readwrite');
					const request = objectStore.put(obj);
					request.onerror = function (): void {
						reject(new Error('Error updating value in IndexedDB'));
					};
					request.onsuccess = resolve;
				} catch (e) {
					reject(e);
				}
			}.bind(this)
		);
	}

	async clearObjectStore(objectStoreName: string): Promise<void> {
		await new Promise(
			async function (resolve: Function, reject: Function): Promise<void> {
				let request;
				try {
					request = (await this.getObjectStore(objectStoreName, 'readwrite')).clear();
					request.onsuccess = resolve;
					request.onerror = function (): void {
						reject(new Error('Error clearing IndexedDB'));
					};
				} catch (e) {
					reject(e);
				}
			}.bind(this)
		);
	}

	async clearObjectStores(objectStoreNames: string[]): Promise<void> {
		for (const objectStoreName of objectStoreNames) {
			try {
				await this.clearObjectStore(objectStoreName);
			} catch (e) {
				SentryLogger.getInstance().logError(e);
				Logger.getInstance().write(
					logConstants.ERROR,
					'Error clearing ' + objectStoreName + ' table. ' + e.message
				);
			}
		}
	}

	async clearAllObjectStores(): Promise<void> {
		await this.clearObjectStores(Object.values(this.db.objectStoreNames));
	}

	closeDBConnection(): void {
		if (this.db) {
			this.db.close();
		}
	}
}

interface DataBaseEventTarget extends EventTarget {
	result: string;
}

interface DataBaseEvent extends Event {
	target: DataBaseEventTarget;
	getMessage(): string;
}
